import {
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
  Box,
} from '@mui/material';
import ArrowDownIcon from '$assets/svgs/arrow-down.svg?react';
import {
  PaymentListOrderBy,
  PaymentListTableHeader,
  headerOrderByMap,
} from '../constant';

const SORTABLE_COLUMNS = [
  PaymentListTableHeader.PatientName,
  PaymentListTableHeader.InvoiceNumber,
  PaymentListTableHeader.Amount,
  PaymentListTableHeader.BillingDate,
];

const getColumnWidth = (header: PaymentListTableHeader): number => {
  switch (header) {
    case PaymentListTableHeader.PatientName:
      return 70;
    case PaymentListTableHeader.InvoiceNumber:
      return 127;
    case PaymentListTableHeader.Status:
      return 100;
    case PaymentListTableHeader.Amount:
      return 100;
    case PaymentListTableHeader.PaymentMethod:
      return 140;
    default:
      return 120;
  }
};

interface HeaderCellProps {
  header: PaymentListTableHeader;
  isSortable: boolean;
  isActive: boolean;
  sortDirection: boolean;
  onSort: () => void;
}

const HeaderCell: React.FC<HeaderCellProps> = ({
  header,
  isSortable,
  isActive,
  sortDirection,
  onSort,
}) => (
  <TableCell
    onClick={isSortable ? onSort : undefined}
    className={`payment-list-header-cell ${isSortable ? 'sortable' : ''}`}
    style={{width: getColumnWidth(header)}}
  >
    <Box className="payment-list-header-content">
      <span>{header}</span>
      {isSortable && (
        <Box
          className={`payment-list-sort-icon ${isActive && sortDirection ? 'flip-vertical' : ''}`}
        >
          <ArrowDownIcon className="primary" />
        </Box>
      )}
    </Box>
  </TableCell>
);

interface PaymentListHeadProps {
  isLoading: boolean;
  sortActiveColumn?: PaymentListOrderBy;
  sortDirection?: boolean;
  setSortActiveColumn: (value?: PaymentListOrderBy) => void;
  setSortDirection: React.Dispatch<React.SetStateAction<boolean>>;
  onSort?: (column: PaymentListOrderBy) => void;
}

function PaymentListHead({
  isLoading,
  sortActiveColumn,
  sortDirection,
  setSortActiveColumn,
  setSortDirection,
}: PaymentListHeadProps) {
  const handleSort = (header: PaymentListTableHeader) => {
    if (sortActiveColumn === headerOrderByMap[header]) {
      setSortDirection(value => !value);
    } else {
      setSortActiveColumn(headerOrderByMap[header]);
      setSortDirection(true);
    }
  };

  const headerCells = Object.values(PaymentListTableHeader).map(header => {
    const isSortable = SORTABLE_COLUMNS.includes(header);
    const isActive = sortActiveColumn === headerOrderByMap[header];

    return (
      <HeaderCell
        key={header}
        header={header}
        isSortable={isSortable}
        isActive={isActive}
        sortDirection={sortDirection ?? false}
        onSort={() => handleSort(header)}
      />
    );
  });

  return (
    <TableHead>
      <TableRow>{headerCells}</TableRow>
      {isLoading && (
        <TableRow>
          <TableCell colSpan={6} className="payment-list-progress-cell">
            <LinearProgress className="payment-list-progress" />
          </TableCell>
        </TableRow>
      )}
    </TableHead>
  );
}

export default PaymentListHead;
